import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Banknote, ArrowRight, Sparkles } from 'lucide-react';
const RAND = new Intl.NumberFormat('en-ZA', {
  style: 'currency',
  currency: 'ZAR',
  maximumFractionDigits: 0
});
const NUM = new Intl.NumberFormat('en-ZA');
interface SliderProps {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}
function Slider({
  label,
  value,
  min,
  max,
  step,
  onChange,
  format
}: SliderProps) {
  const pct = (value - min) / (max - min) * 100;
  return <div data-id="element-327">
      <div className="flex items-center justify-between mb-2" data-id="element-328">
        <label className="text-sm font-medium text-bone-dim" data-id="element-329">{label}</label>
        <span className="text-sm font-semibold text-bone mono" data-id="element-330">
          {format(value)}
        </span>
      </div>
      <input type="range" min={min} max={max} step={step} value={value} onChange={e => onChange(Number(e.target.value))} className="roi-slider w-full h-2 rounded-full appearance-none cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-mint" style={{
      background: `linear-gradient(to right, #2BE38C 0%, #2BE38C ${pct}%, #232427 ${pct}%, #232427 100%)`
    }} aria-label={label} data-id="element-331" />
      
    </div>;
}
export function RoiCalculator() {
  const [accounts, setAccounts] = useState(250000);
  const [avgPayment, setAvgPayment] = useState(650);
  const [currentRate, setCurrentRate] = useState(72);
  const [tillCost, setTillCost] = useState(38);
  const results = useMemo(() => {
    // +37% relative uplift in collection rate, capped at 99%
    const newRate = Math.min(currentRate * 1.37, 99);
    const collectedNow = accounts * avgPayment * (currentRate / 100);
    const collectedNew = accounts * avgPayment * (newRate / 100);
    const extraMonthly = collectedNew - collectedNow;
    const extraAnnual = extraMonthly * 12;
    // Payments that move off the till onto Masube; 80% cheaper to process
    const paymentsHandled = accounts * (newRate / 100);
    const tillCostNow = paymentsHandled * tillCost;
    const masubeCost = paymentsHandled * tillCost * 0.2;
    const savedMonthly = tillCostNow - masubeCost;
    const savedAnnual = savedMonthly * 12;
    const totalImpact = extraAnnual + savedAnnual;
    return {
      newRate,
      extraAnnual,
      savedAnnual,
      totalImpact
    };
  }, [accounts, avgPayment, currentRate, tillCost]);
  return <section id="roi" className="py-24 bg-ink-2 border-y border-white/5" data-id="element-332">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-333">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} className="max-w-3xl mb-16" data-id="element-334">
          
          <div className="text-xs font-medium uppercase tracking-[0.2em] text-mint mb-4" data-id="element-335">
            ROI calculator
          </div>
          <h2 className="display text-bone mb-6" style={{
          fontSize: 'clamp(2.25rem, 5vw, 4rem)'
        }} data-id="element-336">
            
            See what Masube is{' '}
            <span className="display-italic text-mint" data-id="element-337">worth</span> to your
            book.
          </h2>
          <p className="text-lg text-bone-dim leading-relaxed" data-id="element-338">
            Move the sliders to match your account book. We'll project the
            additional revenue collected and the till costs you'd save in a
            year.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-2 gap-6" data-id="element-339">
          {/* Inputs */}
          <div className="bg-ink border border-white/5 rounded-3xl p-8 space-y-7" data-id="element-340">
            <Slider label="Account holders" value={accounts} min={10000} max={2000000} step={10000} onChange={setAccounts} format={v => NUM.format(v)} data-id="element-341" />
            
            <Slider label="Avg. monthly account payment" value={avgPayment} min={100} max={3000} step={50} onChange={setAvgPayment} format={v => RAND.format(v)} data-id="element-342" />
            
            <Slider label="Current on-time collection rate" value={currentRate} min={40} max={95} step={1} onChange={setCurrentRate} format={v => `${v}%`} data-id="element-343" />
            
            <Slider label="Cost per in-store / manual payment" value={tillCost} min={5} max={60} step={1} onChange={setTillCost} format={v => RAND.format(v)} data-id="element-344" />
            
            <p className="text-xs text-muted pt-2 border-t border-white/5" data-id="element-345">
              Estimates assume a +37% relative collection uplift and an 80%
              reduction in per-payment handling cost, based on Masube pilot
              data. Your demo includes a tailored projection.
            </p>
          </div>

          {/* Results */}
          <div className="bg-mint text-ink rounded-3xl p-8 flex flex-col relative overflow-hidden" data-id="element-346">
            <div className="absolute -top-20 -right-20 w-56 h-56 rounded-full bg-ink/5 blur-2xl pointer-events-none" data-id="element-347"></div>
            <div className="relative z-10 flex-1 flex flex-col" data-id="element-348">
              <div className="flex items-center gap-2 text-ink/70 text-xs font-semibold uppercase tracking-[0.2em] mb-6" data-id="element-349">
                <Sparkles className="w-4 h-4" data-id="element-350" />
                Projected annual impact
              </div>

              <motion.div key={results.totalImpact} initial={{
              opacity: 0.4,
              y: 6
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.3
            }} className="display text-ink mb-2" style={{
              fontSize: 'clamp(2.5rem, 6vw, 4.5rem)',
              lineHeight: 1
            }} data-id="element-351">
                
                {RAND.format(results.totalImpact)}
              </motion.div>
              <p className="text-ink/70 mb-8" data-id="element-352">
                in additional collections + cost savings per year
              </p>

              <div className="space-y-4 mt-auto" data-id="element-353">
                <div className="flex items-center justify-between py-4 border-t border-ink/15" data-id="element-354">
                  <div className="flex items-center gap-3" data-id="element-355">
                    <div className="w-10 h-10 rounded-xl bg-ink/10 flex items-center justify-center" data-id="element-356">
                      <TrendingUp className="w-5 h-5" data-id="element-357" />
                    </div>
                    <div data-id="element-358">
                      <div className="text-sm font-medium text-ink" data-id="element-359">
                        Extra collected / year
                      </div>
                      <div className="text-xs text-ink/60" data-id="element-360">
                        at a {results.newRate.toFixed(0)}% collection rate
                      </div>
                    </div>
                  </div>
                  <div className="text-xl font-semibold text-ink" data-id="element-361">
                    {RAND.format(results.extraAnnual)}
                  </div>
                </div>

                <div className="flex items-center justify-between py-4 border-t border-ink/15" data-id="element-362">
                  <div className="flex items-center gap-3" data-id="element-363">
                    <div className="w-10 h-10 rounded-xl bg-ink/10 flex items-center justify-center" data-id="element-364">
                      <Banknote className="w-5 h-5" data-id="element-365" />
                    </div>
                    <div data-id="element-366">
                      <div className="text-sm font-medium text-ink" data-id="element-367">
                        Till costs saved / year
                      </div>
                      <div className="text-xs text-ink/60" data-id="element-368">
                        80% cheaper per payment
                      </div>
                    </div>
                  </div>
                  <div className="text-xl font-semibold text-ink" data-id="element-369">
                    {RAND.format(results.savedAnnual)}
                  </div>
                </div>

                <a href="#book-demo" className="mt-4 inline-flex items-center justify-center gap-2 w-full py-4 rounded-2xl bg-ink text-bone font-semibold text-sm hover:bg-ink-2 transition-colors" data-id="element-370">
                  
                  Get this projection for your book
                  <ArrowRight className="w-4 h-4" data-id="element-371" />
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}